import { SectionHeader } from '@core/components';
import type { CustomLabel } from '@core/graphql/types';
import { Badge, BadgeVariant, ButtonType, Heading } from '@soteria/design-system-ve';
import { SortableDataList, type SortableTableColumn } from '@system-config/label-management/components/SortableDataList';
import { useDeleteCustomLabel, useCustomLabels } from '@system-config/label-management/hooks';
import { useMemo, useState } from 'react';

import { ModerationLabelDialog } from '../ModerationLabelDialog';

import * as styles from './ModerationLabelSection.css';

interface ModerationLabelSectionProps {
  communityId: string;
}

/**
 * Moderation Label section component for use within tabs.
 * Displays custom moderation labels management with CRUD operations.
 */
export function ModerationLabelSection({ communityId }: ModerationLabelSectionProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<CustomLabel | undefined>(undefined);

  const { labels, loading, errors } = useCustomLabels(communityId);
  const { deleteCustomLabel, loading: isDeleting, errors: deleteErrors, resetErrors } = useDeleteCustomLabel(communityId);

  const handleOpenDialog = (label?: CustomLabel) => {
    setSelectedLabel(label || undefined);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedLabel(undefined);
  };

  // Filter to only show moderation labels (case-insensitive)
  const moderationLabels = useMemo(() => labels.filter(label => label.labelType?.toLowerCase() === 'moderation'), [labels]);

  // Create the delete item context for custom labels
  const deleteItemContext = {
    handler: async (label: CustomLabel) => {
      const result = await deleteCustomLabel(label.id);

      return result;
    },
    isDeleting,
    errors: deleteErrors || null,
    resetErrors,
  };

  // Custom Labels columns
  const customLabelsColumns: SortableTableColumn<CustomLabel>[] = [
    {
      key: 'name',
      header: 'Label Name',
      render: item => item.name,
      width: '30%',
      sortable: true,
      sortKey: 'name',
    },
    {
      key: 'description',
      header: 'Description',
      render: item => item.description,
      width: '50%',
      sortable: true,
      sortKey: 'description',
    },
    {
      key: 'active',
      header: 'Status',
      render: item => {
        const variant = item.active ? BadgeVariant.Approved : BadgeVariant.Default;

        return <Badge text={item.active ? 'Active' : 'Inactive'} variant={variant} />;
      },
      width: '20%',
      sortable: true,
      sortKey: item => (item.active ? 'Active' : 'Inactive'),
    },
  ];

  return (
    <>
      <SectionHeader
        action={{
          text: 'ADD LABEL',
          onClick: () => handleOpenDialog(undefined),
          buttonType: ButtonType.Primary,
        }}
        description="Configure moderation labels that can be applied to content during moderation actions."
        title={`Moderation Labels (${moderationLabels.length})`}
      />

      {/* Custom Moderation Labels Section */}
      <div className={styles.customLabelsSection}>
        <Heading level="h3">Custom Moderation Labels</Heading>
        <SortableDataList
          searchable
          showFilterBar
          columns={customLabelsColumns}
          data={moderationLabels}
          dataTypeName="moderation label"
          defaultSort={{ key: 'name', direction: 'asc' }}
          deleteItemContext={deleteItemContext}
          error={errors}
          isLoading={loading}
          searchKey="name"
          searchPlaceholder="Search by label name..."
          sortOptions={[
            { value: 'name', label: 'Label Name', sortKey: 'name' },
            { value: 'description', label: 'Description', sortKey: 'description' },
            { value: 'active', label: 'Status', sortKey: item => (item.active ? 'Active' : 'Inactive') },
          ]}
          onEdit={handleOpenDialog}
        />
      </div>

      {/* Add/Edit Label Dialog */}
      <ModerationLabelDialog communityId={communityId} isOpen={isDialogOpen} label={selectedLabel} onClose={handleCloseDialog} />
    </>
  );
}
